import React, { useState } from 'react';
import { Link, ListItem, UnorderedList } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { LegalSection, LegalShell, P, TranslationNotice } from './LegalShell';
import { readLang, writeLang } from './siteLang';

/* ============================================================
   英文版：Privacy Policy（原文，未改动）
   ============================================================ */
const EnBody = () => (
  <>
    <P>
      Aiker Inc. ("Aiker," "we," "us," or "our") provides customer support, communications, and
      scheduling software and related services (the "Services") to businesses ("Customers"). This
      Privacy Policy explains how we collect, use, disclose, and protect personal information when you
      visit our website at bizsprite.com (the "Site"), use the Services as an authorized user of a
      Customer ("User"), or otherwise interact with the Services.
    </P>
    <P>
      By accessing or using the Site or Services, you acknowledge that you have read and understood this
      Privacy Policy.
    </P>

    <LegalSection title="1. Information We Collect">
      <P mb={3}>We may collect the following categories of personal information:</P>
      <UnorderedList pl={4} spacing={3} color="gray.700">
        <ListItem><strong>Account Information.</strong> Name, email address, phone number, company information, and login credentials.</ListItem>
        <ListItem><strong>Google User Data.</strong> If you choose to connect your Google account to the Services, we may access certain information from your Google account as authorized by you through the Google OAuth consent process. Depending on the permissions you grant, this may include calendar information, contact information, and basic profile information.</ListItem>
        <ListItem><strong>Payment Information.</strong> Payment card information is processed by our third-party payment processor, Stripe. We do not store full payment card numbers and instead retain only limited payment-related information and tokenized references as provided by our processor.</ListItem>
        <ListItem><strong>Communications with Us.</strong> Information you provide when you contact us for support, request information, or otherwise communicate with us.</ListItem>
        <ListItem><strong>Call Audio and Transcriptions.</strong> Audio recordings, call metadata, and transcripts generated in connection with the Services.</ListItem>
        <ListItem><strong>Usage Data.</strong> Internet Protocol address, browser type, device information, operating system, referring pages, timestamps, and similar usage information.</ListItem>
        <ListItem><strong>Cookies and Similar Technologies.</strong> Information collected through cookies and similar technologies to operate, secure, and analyze the use of the Site and Services.</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="2. Google User Data and Limited Use Disclosure">
      <P mb={3}>
        Aiker’s use and transfer of information received from Google APIs will comply with the Google API
        Services User Data Policy, including the Limited Use requirements.
      </P>
      <P mb={3}>
        We access and use Google User Data only as authorized by the user and only for the limited
        purposes described in this Privacy Policy and in the applicable consent flow.
      </P>
      <P mb={3}>Specifically, we use Google User Data only to:</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>provide user-authorized functionality within the Services, such as checking calendar availability, scheduling or updating appointments, and supporting user-directed contact or communications workflows;</ListItem>
        <ListItem>maintain and support the Services, including troubleshooting, service reliability, security monitoring, abuse prevention, and fraud prevention; and</ListItem>
        <ListItem>comply with applicable law, regulation, legal process, or enforceable governmental request.</ListItem>
      </UnorderedList>
      <P mt={3} mb={3}>We do not use Google User Data for:</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>advertising, including targeted advertising, retargeting, personalized advertising, or interest-based advertising;</ListItem>
        <ListItem>the sale of personal information;</ListItem>
        <ListItem>marketing profiling unrelated to the user-authorized functionality of the Services;</ListItem>
        <ListItem>any unrelated commercial purpose; or</ListItem>
        <ListItem>generalized AI or machine learning model training.</ListItem>
      </UnorderedList>
      <P mt={3}>
        We do not permit humans to read Google User Data except where:
        (i) we have the user’s affirmative consent for specific content,
        (ii) it is necessary for security purposes, such as investigating abuse or unauthorized access,
        (iii) it is required by applicable law, or
        (iv) it is necessary to provide or maintain the Services in accordance with applicable law and applicable Google API requirements.
      </P>
    </LegalSection>

    <LegalSection title="3. How We Use Personal Information">
      <P mb={3}>We may use personal information to:</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>provide, operate, maintain, and support the Services;</ListItem>
        <ListItem>authenticate users and administer accounts;</ListItem>
        <ListItem>process transactions and manage billing;</ListItem>
        <ListItem>facilitate communications and scheduling functions requested by users or Customers;</ListItem>
        <ListItem>monitor, secure, troubleshoot, and improve the reliability and functionality of the Services;</ListItem>
        <ListItem>respond to support requests and communicate with users about their accounts or the Services; and</ListItem>
        <ListItem>comply with legal obligations, protect our rights, and prevent fraud, misuse, or other harmful activity.</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="4. Information Sharing">
      <P mb={3}>We may share personal information only in the following circumstances:</P>
      <UnorderedList pl={4} spacing={3} color="gray.700">
        <ListItem><strong>Customers.</strong> We may share data processed through the Services with the Customer that owns or administers the relevant Service environment.</ListItem>
        <ListItem><strong>Service Providers.</strong> We may share information with vendors and service providers that perform services on our behalf, such as hosting, infrastructure, payment processing, communications support, customer support, analytics, and security services. These providers are authorized to access and use personal information only as necessary to provide services to us and are contractually required to protect such information.</ListItem>
        <ListItem><strong>Legal Compliance and Protection.</strong> We may disclose information if required to do so by law, regulation, legal process, or governmental request, or when we believe disclosure is necessary to protect the rights, safety, and security of Aiker, our Customers, our users, or others.</ListItem>
        <ListItem><strong>Business Transfers.</strong> We may transfer information as part of a merger, acquisition, financing, reorganization, sale of assets, or similar corporate transaction, subject to applicable confidentiality and legal restrictions.</ListItem>
      </UnorderedList>
      <P mt={3} fontWeight="semibold">We do not sell Google User Data to third parties.</P>
    </LegalSection>

    <LegalSection title="5. Data Retention">
      <P>
        We retain personal information only for as long as reasonably necessary to fulfill the purposes
        for which it was collected, as described in this Privacy Policy, unless a longer retention period
        is required or permitted by law.
      </P>
      <P mt={3}>
        <strong>Google User Data.</strong> We retain Google User Data only for as long as reasonably
        necessary to maintain the active, user-authorized integration and provide the related
        functionality of the Services.
      </P>
      <P mt={3}>
        If a user disconnects their Google account, revokes access, or terminates the relevant account or
        service relationship, we will stop accessing new Google User Data and will delete or de-identify
        previously stored Google User Data within a reasonable period, subject to applicable law and the
        exceptions described below.
      </P>
      <P mt={3}>We may retain certain information for a longer period where necessary to:</P>
      <UnorderedList pl={4} spacing={2} color="gray.700" mt={2}>
        <ListItem>comply with legal, tax, accounting, audit, or regulatory obligations;</ListItem>
        <ListItem>detect, investigate, or prevent security incidents, fraud, abuse, or other unlawful activity;</ListItem>
        <ListItem>resolve disputes;</ListItem>
        <ListItem>enforce our agreements; or</ListItem>
        <ListItem>maintain appropriate business and financial records.</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="6. Data Deletion and User Choices">
      <P>
        <strong>Deletion Requests.</strong> Users may request deletion of their personal information,
        including Google User Data, by contacting us at support@bizsprite.com. We will review and process
        such requests in accordance with applicable law and our internal data retention and deletion
        procedures.
      </P>
      <P mt={3}>
        <strong>Revoking Google Access.</strong> Users may revoke Aiker’s access to your Google account at
        any time through their Google account permissions settings at{' '}
        <Link href="https://myaccount.google.com/permissions" isExternal color="blue.600">https://myaccount.google.com/permissions</Link>.
        Once access is revoked, we will no longer access new Google User Data from that account and will
        initiate deletion or de-identification of previously stored Google User Data within a reasonable
        period, except where retention is permitted or required for legal, security, fraud prevention,
        dispute resolution, or compliance purposes.
      </P>
      <P mt={3}>
        <strong>Account and Communication Choices.</strong> Users may also update certain account
        information or communication preferences through their account settings, where available, or by
        contacting us.
      </P>
    </LegalSection>

    <LegalSection title="7. Security">
      <P>
        We implement reasonable administrative, technical, and physical safeguards designed to protect
        personal information against unauthorized access, disclosure, alteration, and destruction.
        However, no method of transmission over the Internet or method of electronic storage is completely
        secure, and we cannot guarantee absolute security.
      </P>
    </LegalSection>

    <LegalSection title="8. International Data Transfers">
      <P>
        Aiker is based in the United States, and personal information may be transferred to, stored in,
        and processed in the United States or other jurisdictions where we or our service providers
        operate. By using the Services, you understand that your information may be transferred to
        jurisdictions that may have different data protection laws than those of your country or region.
      </P>
    </LegalSection>

    <LegalSection title="9. Children’s Privacy">
      <P>
        The Services are not directed to children under the age of 13, and we do not knowingly collect
        personal information directly from children under 13. If we become aware that we have collected
        such information without appropriate authorization, we will take reasonable steps to delete it.
      </P>
    </LegalSection>

    <LegalSection title="10. Changes to This Privacy Policy">
      <P>
        We may update this Privacy Policy from time to time to reflect changes in our practices,
        technologies, legal requirements, or other operational needs. When we do, we will revise the
        “Last Updated” date at the top of this Privacy Policy. Your continued use of the Services after
        any update becomes effective constitutes your acceptance of the revised Privacy Policy, to the
        extent permitted by law.
      </P>
    </LegalSection>

    <LegalSection title="11. Contact Us">
      <P mb={3}>
        If you have any questions or requests regarding this Privacy Policy or our data practices, please
        contact us at:
      </P>
      <P fontWeight="semibold">
        Aiker Inc.<br />
        Email: support@bizsprite.com
      </P>
    </LegalSection>
  </>
);

/* ============================================================
   中文版：英文原文的对照译文（页面顶部已声明「以英文版为准」）
   ============================================================ */
const ZhBody = () => (
  <>
    <P>
      Aiker Inc.（以下简称「Aiker」「我们」）为企业客户（以下简称「客户」）提供客户支持、通信与预约类软件及相关服务（以下简称「本服务」）。
      本隐私政策说明：当你访问我们的网站 bizsprite.com（以下简称「本网站」）、作为客户授权用户（以下简称「用户」）使用本服务，
      或以其他方式与本服务交互时，我们如何收集、使用、披露和保护个人信息。
    </P>
    <P>访问或使用本网站或本服务，即表示你已阅读并理解本隐私政策。</P>

    <LegalSection title="一、我们收集的信息">
      <P mb={3}>我们可能收集以下几类个人信息：</P>
      <UnorderedList pl={4} spacing={3} color="gray.700">
        <ListItem><strong>账户信息。</strong>姓名、电子邮箱、电话号码、公司信息及登录凭据。</ListItem>
        <ListItem><strong>Google 用户数据。</strong>如果你选择将 Google 账户连接到本服务，我们可能在你通过 Google OAuth 授权流程许可的范围内，访问你 Google 账户中的部分信息。根据你授予的权限，这可能包括日历信息、联系人信息与基本资料信息。</ListItem>
        <ListItem><strong>支付信息。</strong>支付卡信息由我们的第三方支付处理商 Stripe 处理。我们不存储完整的支付卡号，仅保留支付处理商提供的有限支付相关信息与令牌化引用。</ListItem>
        <ListItem><strong>与我们的沟通记录。</strong>你在联系客服、索取资料或以其他方式与我们沟通时提供的信息。</ListItem>
        <ListItem><strong>通话音频与转写文本。</strong>与本服务相关的通话录音、通话元数据与转写文本。</ListItem>
        <ListItem><strong>使用数据。</strong>IP 地址、浏览器类型、设备信息、操作系统、来源页面、时间戳及类似使用信息。</ListItem>
        <ListItem><strong>Cookie 及类似技术。</strong>通过 Cookie 及类似技术收集的信息，用于运行、保护和分析本网站与本服务的使用情况。</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="二、Google 用户数据与有限使用声明">
      <P mb={3}>
        Aiker 对通过 Google API 获取的信息的使用与传输，将遵守《Google API 服务用户数据政策》，包括其中的有限使用要求。
      </P>
      <P mb={3}>我们仅在用户授权范围内、且仅出于本隐私政策与相应授权流程所述的有限目的，访问和使用 Google 用户数据。</P>
      <P mb={3}>具体而言，我们使用 Google 用户数据仅用于：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>在本服务内提供经用户授权的功能，例如查询日历空闲时间、创建或更新预约，以及支持由用户发起的联系或通信流程；</ListItem>
        <ListItem>维护与支持本服务，包括故障排查、服务可靠性、安全监控、滥用防范与欺诈防范；以及</ListItem>
        <ListItem>遵守适用法律、法规、法律程序或有强制执行力的政府要求。</ListItem>
      </UnorderedList>
      <P mt={3} mb={3}>我们不会将 Google 用户数据用于：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>广告用途，包括定向广告、再营销、个性化广告或基于兴趣的广告；</ListItem>
        <ListItem>出售个人信息；</ListItem>
        <ListItem>与本服务经用户授权功能无关的营销画像；</ListItem>
        <ListItem>任何无关的商业目的；或</ListItem>
        <ListItem>通用 AI 或机器学习模型训练。</ListItem>
      </UnorderedList>
      <P mt={3}>
        除以下情形外，我们不允许人工读取 Google 用户数据：
        （i）我们已就特定内容取得用户的明确同意；
        （ii）出于安全目的所必需，例如调查滥用或未经授权的访问；
        （iii）适用法律要求；或
        （iv）为依照适用法律及适用的 Google API 要求提供或维护本服务所必需。
      </P>
    </LegalSection>

    <LegalSection title="三、我们如何使用个人信息">
      <P mb={3}>我们可能将个人信息用于：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700">
        <ListItem>提供、运营、维护与支持本服务；</ListItem>
        <ListItem>验证用户身份并管理账户；</ListItem>
        <ListItem>处理交易并管理账单；</ListItem>
        <ListItem>实现用户或客户所请求的通信与预约功能；</ListItem>
        <ListItem>监控、保护、排查并改进本服务的可靠性与功能；</ListItem>
        <ListItem>响应支持请求，并就其账户或本服务与用户沟通；以及</ListItem>
        <ListItem>遵守法律义务、保护我们的权利，并防范欺诈、滥用或其他有害行为。</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="四、信息共享">
      <P mb={3}>我们仅在以下情形下共享个人信息：</P>
      <UnorderedList pl={4} spacing={3} color="gray.700">
        <ListItem><strong>客户。</strong>我们可能将本服务处理的数据共享给拥有或管理相关服务环境的客户。</ListItem>
        <ListItem><strong>服务提供商。</strong>我们可能将信息共享给代表我们提供服务的供应商与服务商，例如托管、基础设施、支付处理、通信支持、客户支持、数据分析与安全服务。这些服务商仅可在为我们提供服务所必需的范围内访问和使用个人信息，并须按合同约定保护该等信息。</ListItem>
        <ListItem><strong>法律合规与权益保护。</strong>如法律、法规、法律程序或政府要求作出披露，或我们认为披露对于保护 Aiker、我们的客户、用户或其他人的权利、安全与保障确有必要时，我们可能披露相关信息。</ListItem>
        <ListItem><strong>业务转让。</strong>在合并、收购、融资、重组、资产出售或类似公司交易中，我们可能转让相关信息，但须遵守适用的保密义务与法律限制。</ListItem>
      </UnorderedList>
      <P mt={3} fontWeight="semibold">我们不会向第三方出售 Google 用户数据。</P>
    </LegalSection>

    <LegalSection title="五、数据保留">
      <P>
        除法律要求或允许更长的保留期限外，我们仅在实现本隐私政策所述收集目的所合理必要的期限内保留个人信息。
      </P>
      <P mt={3}>
        <strong>Google 用户数据。</strong>我们仅在维持有效的、经用户授权的集成并提供本服务相关功能所合理必要的期限内保留 Google 用户数据。
      </P>
      <P mt={3}>
        如用户断开其 Google 账户、撤销授权，或终止相关账户或服务关系，我们将停止访问新的 Google 用户数据，并在合理期限内删除或去标识化此前存储的 Google 用户数据，
        但须遵守适用法律及下述例外情形。
      </P>
      <P mt={3}>在下列情形下，我们可能将部分信息保留更长时间：</P>
      <UnorderedList pl={4} spacing={2} color="gray.700" mt={2}>
        <ListItem>遵守法律、税务、会计、审计或监管义务；</ListItem>
        <ListItem>发现、调查或防范安全事件、欺诈、滥用或其他违法行为；</ListItem>
        <ListItem>解决争议；</ListItem>
        <ListItem>执行我们的协议；或</ListItem>
        <ListItem>维护适当的业务与财务记录。</ListItem>
      </UnorderedList>
    </LegalSection>

    <LegalSection title="六、数据删除与用户选择">
      <P>
        <strong>删除请求。</strong>用户可通过 support@bizsprite.com 联系我们，请求删除其个人信息（包括 Google 用户数据）。
        我们将依照适用法律及内部数据保留与删除流程审核并处理此类请求。
      </P>
      <P mt={3}>
        <strong>撤销 Google 授权。</strong>用户可随时通过 Google 账户的权限设置页面{' '}
        <Link href="https://myaccount.google.com/permissions" isExternal color="blue.600">https://myaccount.google.com/permissions</Link>{' '}
        撤销 Aiker 对其 Google 账户的访问权限。授权撤销后，我们将不再从该账户访问新的 Google 用户数据，
        并将在合理期限内启动对此前存储的 Google 用户数据的删除或去标识化处理；
        但出于法律、安全、欺诈防范、争议解决或合规目的而允许或要求保留的情形除外。
      </P>
      <P mt={3}>
        <strong>账户与通信选择。</strong>用户也可在可用的情况下通过账户设置，或联系我们来更新部分账户信息或通信偏好。
      </P>
    </LegalSection>

    <LegalSection title="七、安全">
      <P>
        我们采取合理的管理、技术与物理防护措施，以保护个人信息免遭未经授权的访问、披露、篡改与销毁。
        但通过互联网传输或电子存储的任何方式都无法做到绝对安全，我们无法保证绝对的安全性。
      </P>
    </LegalSection>

    <LegalSection title="八、跨境数据传输">
      <P>
        Aiker 位于美国，个人信息可能被传输至、存储于并在美国或我们及服务提供商开展业务的其他司法辖区处理。
        使用本服务，即表示你理解你的信息可能被传输至与你所在国家或地区数据保护法律不同的司法辖区。
      </P>
    </LegalSection>

    <LegalSection title="九、儿童隐私">
      <P>
        本服务不面向 13 岁以下儿童，我们也不会在知情情况下直接收集 13 岁以下儿童的个人信息。
        如我们发现在未获得适当授权的情况下收集了此类信息，将采取合理措施予以删除。
      </P>
    </LegalSection>

    <LegalSection title="十、本政策的变更">
      <P>
        我们可能不时更新本隐私政策，以反映我们在做法、技术、法律要求或其他运营需要方面的变化。
        更新时，我们会修改本隐私政策顶部的「最后更新」日期。在法律允许的范围内，更新生效后你继续使用本服务，即构成对修订后隐私政策的接受。
      </P>
    </LegalSection>

    <LegalSection title="十一、联系我们">
      <P mb={3}>如你对本隐私政策或我们的数据实践有任何疑问或请求，请通过以下方式联系我们：</P>
      <P fontWeight="semibold">
        Aiker Inc.<br />
        电子邮箱：support@bizsprite.com
      </P>
    </LegalSection>
  </>
);

const PrivacyPolicy = () => {
  const [lang, setLangState] = useState(readLang);
  const setLang = (l) => {
    setLangState(l);
    writeLang(l);
  };
  const zh = lang === 'zh';

  return (
    <LegalShell
      lang={lang}
      setLang={setLang}
      title={zh ? '隐私政策' : 'Privacy Policy'}
      updated={zh ? '最后更新：2026 年 4 月 6 日（中文译文）' : 'Last Updated: April 6, 2026'}
      notice={<TranslationNotice lang={lang} />}
      footerExtra={
        <Link as={RouterLink} to="/terms" color="blue.500">
          {zh ? '服务条款' : 'Terms and Conditions'}
        </Link>
      }
    >
      {zh ? <ZhBody /> : <EnBody />}
    </LegalShell>
  );
};

export default PrivacyPolicy;
